import { Box, Divider, Typography } from '@mui/material';
import React from 'react';
const CodeBlock = ({ html }: { html: React.ReactNode }) => (
  <Box
    component="pre"
    sx={{
      backgroundColor: 'grey.100',
      borderRadius: 1,
      p: 2,
      overflowX: 'auto',
      fontSize: '0.85rem',
      fontFamily: 'monospace',
      mt: 1,
      mb: 2,
    }}
    dangerouslySetInnerHTML={{ __html: html as string }}
  />
);

const tutorial1Code = `# Step 1: Look up the Shopify report ID
curl -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/suggestions?search=<b>Shopify</b>"

/**
 *  // Response example
 *  {
 *     "name": "Shopify",
 *     "uuid": <b>"d513e568-ad32-44b5-b0c8-1b7d3fbe88a6"</b>,
 *     ...
 *  }
 * 
 */

# Step 2: Search using the report ID returned above
curl -G -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/search" \\
  --data-urlencode 'search={"all":[{"type":"report","value":"<b>d513e568-ad32-44b5-b0c8-1b7d3fbe88a6</b>"}]}'`;

const tutorial2Code = `# Step 1: Get report IDs for each technology
curl -H "api_key: <b>YOUR_API_KEY</b>" "https://api.nerdydata.com/suggestions?search=<b>Shopify</b>"
curl -H "api_key: <b>YOUR_API_KEY</b>" "https://api.nerdydata.com/suggestions?search=<b>Klaviyo</b>"
curl -H "api_key: <b>YOUR_API_KEY</b>" "https://api.nerdydata.com/suggestions?search=<b>Mailchimp</b>"

# Step 2: Search using all/none filters (replace IDs with values from step 1)
curl -G -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/search" \\
  --data-urlencode 'search={"<b>all</b>":[{"type":"report","value":"<b>shopify-id</b>"},{"type":"report","value":"<b>klaviyo-id</b>"}],"<b>none</b>":[{"type":"report","value":"<b>mailchimp-id</b>"}]}'

# Step 3: Export the full list as a CSV
curl -X <b>POST</b> \\
  -H "api_key: <b>YOUR_API_KEY</b>" \\
  -H "Content-Type: application/json" \\
  "https://api.nerdydata.com/<b>downloads</b>" \\
  -d '{"search":{"<b>all</b>":[{"type":"report","value":"<b>shopify-id</b>"},{"type":"report","value":"<b>klaviyo-id</b>"}],"<b>none</b>":[{"type":"report","value":"<b>mailchimp-id</b>"}]}}'

/**
 * // Response example
 * {
 *   "hash": "<b>1d6b4b14-28a9-4386-ad67-19fc4b577f9b</b>",
 *   "file": "" // upon completion, the file URL will be returned here
 * }
 * 
 */
# Step 4: Poll every 30 seconds until the file is ready (use the hash from step 3)
curl -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/downloads/<b>1d6b4b14-28a9-4386-ad67-19fc4b577f9b</b>"`;

const tutorial3Code = `curl -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/domains/<b>allbirds.com</b>/technologies"`;

const tutorial4Code = `# Search for websites containing a specific code snippet
curl -G -H "api_key: <b>YOUR_API_KEY</b>" \\
  "https://api.nerdydata.com/search" \\
  --data-urlencode 'search={"all":[{"type":"<b>code</b>","value":"<b>cdn.shopify.com</b>"}]}'`;

export const ApiTutorials = () => {
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          How many websites use Shopify?
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Use the <code>/suggestions</code> endpoint to look up a technology
          report ID by name, then pass it to <code>/search</code> to get the
          total count and a sample of matching websites.
        </Typography>
        <CodeBlock html={tutorial1Code} />
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Find Shopify stores that use Klaviyo but not Mailchimp
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Combine multiple report IDs in a single search using <code>all</code>{' '}
          (must match) and <code>none</code> (must not match) filters. Then
          queue a full CSV export with <code>/downloads</code> and poll for the
          file URL.
        </Typography>
        <CodeBlock html={tutorial2Code} />
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          What technologies does allbirds.com use?
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Look up any domain&apos;s technology stack with the{' '}
          <code>/domains/{'{domain}'}/technologies</code> endpoint. Returns the
          full list of detected technologies along with category and industry
          metadata.
        </Typography>
        <CodeBlock html={tutorial3Code} />
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Which websites have this snippet of code?
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Search the raw source code, variables, cookies, and headers of crawled
          sites with a <code>code</code> type filter on <code>/search</code>.
          Useful for finding sites that reference a specific script, domain, or
          fingerprint string.
        </Typography>
        <CodeBlock html={tutorial4Code} />
      </Box>
    </Box>
  );
};
